import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { NavBar } from 'antd-mobile'
import './index.scss'

interface AlbumItem {
  title?: string
  introduce?: string
  imgUrl?: string[]
  video?: string[]
}

export default function AlbumDetails() {
  const navigate = useNavigate()
  const location = useLocation()
  const { item }: { item?: AlbumItem } = location.state || {}

  // 将图片和视频合并到一个数组
  const mediaList: string[] = (item?.imgUrl || []).concat(item?.video || [])

  // 全屏查看逻辑
  const [viewerVisible, setViewerVisible] = useState(false)
  const [viewerIndex, setViewerIndex] = useState(0)

  // 网格中视频的引用，用于手动控制播放
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])

  // 判断是否为视频
  const isVideo = (src: string) => /\.(mp4|webm|ogg)$/i.test(src)

  // 点击某个媒体 => 打开全屏
  const openViewer = (index: number) => {
    setViewerIndex(index)
    setViewerVisible(true)
  }

  // 关闭全屏
  const closeViewer = () => {
    setViewerVisible(false)
    // 关闭全屏时暂停所有网格视频
    videoRefs.current.forEach((video) => {
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    })
  }

  // 渲染网格中的媒体（为视频添加 preload 和 webkit-playsinline 属性）
  const renderMedia = (src: string, index: number) => {
    if (isVideo(src)) {
      return (
        <video
          ref={(el) => {
            videoRefs.current[index] = el
          }}
          src={"http://175.27.130.210:8091"+src}
          className="media-item"
          muted
          playsInline
          autoPlay
          preload="auto"
          // 添加该属性以提高安卓兼容性
          webkit-playsinline="true"
        />
      )
    }
    return <img src={"http://175.27.130.210:8091"+src} className="media-item" alt="" />
  }

  // 当全屏查看器打开时，暂停所有网格视频并自动播放当前全屏视频
  useEffect(() => {
    if (!viewerVisible) return;
    // 先暂停所有网格视频
    videoRefs.current.forEach((video) => {
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    })
    // 如果当前媒体是视频，则自动播放
    if (isVideo(mediaList[viewerIndex])) {
      const curVideo = videoRefs.current[viewerIndex]
      if (curVideo) {
        curVideo.play().catch((err) => {
          console.log('全屏视频自动播放失败', err)
        })
      }
    }
  }, [viewerIndex, mediaList, viewerVisible])

  // 当全屏未打开时，尝试手动播放网格内视频（解决部分安卓设备自动播放失败的问题）
  useEffect(() => {
    if (!viewerVisible) {
      videoRefs.current.forEach((video) => {
        if (video && video.paused) {
          video.muted = true
          video.play().catch((err) => {
            console.log('网格视频自动播放错误', err)
          })
        }
      })
    }
  }, [viewerVisible, mediaList])

  return (
    <div className="album-details">
      <NavBar onBack={() => navigate(-1)}>相册详情</NavBar>

      {/* 网格区域：2列显示，不裁剪图片/视频 */}
      <div className="media-grid">
        {mediaList.map((src, index) => (
          <div
            key={index}
            className="media-wrapper"
            onClick={() => openViewer(index)}
          >
            {renderMedia(src, index)}
          </div>
        ))}
      </div>

      {/* 底部文字 */}
      <div className="content-box">
        <div className="content-box-title">{item?.title}</div>
        <div className="description">{item?.introduce}</div>
      </div>

      {/* 全屏查看器 */}
      {viewerVisible && (
        <CustomViewer
          mediaList={mediaList}
          defaultIndex={viewerIndex}
          onClose={closeViewer}
          onIndexChange={(i) => setViewerIndex(i)}
        />
      )}
    </div>
  )
}

/**
 * 自定义全屏查看器
 */
interface CustomViewerProps {
  mediaList: string[]
  defaultIndex: number
  onClose: () => void
  onIndexChange: (index: number) => void
}

function CustomViewer(props: CustomViewerProps) {
  const { mediaList, defaultIndex, onClose, onIndexChange } = props
  const [currentIndex, setCurrentIndex] = useState(defaultIndex)

  const startXRef = useRef(0)
  const currentTranslateRef = useRef(0)
  // 为全屏视频添加 ref 以便控制取消静音
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    onIndexChange(currentIndex)
  }, [currentIndex, onIndexChange])

  // 如果当前媒体是视频，则在渲染后取消静音并播放
  useEffect(() => {
    if (isVideo(mediaList[currentIndex]) && videoRef.current) {
      videoRef.current.muted = false
      videoRef.current.play().catch((err) => {
        console.log('带声音播放失败', err)
      })
    }
  }, [currentIndex, mediaList])

  // 触摸开始
  const handleTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX
    currentTranslateRef.current = 0
  }

  // 触摸移动
  const handleTouchMove = (e: React.TouchEvent) => {
    const deltaX = e.touches[0].clientX - startXRef.current
    currentTranslateRef.current = deltaX
  }

  // 触摸结束
  const handleTouchEnd = () => {
    const threshold = 50
    const deltaX = currentTranslateRef.current
    if (deltaX < -threshold) {
      // 左滑 => 下一张
      if (currentIndex < mediaList.length - 1) {
        setCurrentIndex((prev) => prev + 1)
      }
    } else if (deltaX > threshold) {
      // 右滑 => 上一张
      if (currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1)
      }
    }
  }

  // 判断是否为视频
  const isVideo = (src: string) => /\.(mp4|webm|ogg)$/i.test(src)

  return (
    <div className="custom-viewer" onClick={onClose}>
      {/* 显示当前页码 */}
      <div className="viewer-counter">
        {currentIndex + 1} / {mediaList.length}
      </div>

      <div
        className="viewer-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isVideo(mediaList[currentIndex]) ? (
          <video
            ref={videoRef}
            src={"http://175.27.130.210:8091"+mediaList[currentIndex]}
            className="viewer-video"
            playsInline
            autoPlay
            muted
            preload="auto"
            webkit-playsinline="true"
          />
        ) : (
          <img
            src={"http://175.27.130.210:8091"+mediaList[currentIndex]}
            className="viewer-image"
            alt=""
          />
        )}
      </div>
    </div>
  )
}
